<script>
  import { tourFunctions } from "../tour-functions";
  import { fly } from "svelte/transition";
  import { focusTrap } from "./utils.js";
  import { Preloader } from "./preloader.js";
  import i18n from "./i18n/i18n.js";
  import { setTrackSceneSource } from "./analytics.js";
  import AiIcon from "./icons/AiIcon.svelte";
  import OpenAIIcon from "./icons/OpenAIIcon.svelte";
  import { onDestroy } from 'svelte';

  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  import Tooltip from "./Tooltip.svelte";
  import { onMount, tick } from "svelte";
  let showingThumbsTooltip = false;
  const showThumbsTooltip = () => {
    showingThumbsTooltip = true;
  };
  const hideThumbsTooltip = () => {
    showingThumbsTooltip = false;
  };

  let showingPrevTooltip = false;
  const showPrevTooltip = () => {
    showingPrevTooltip = true;
  };
  const hidePrevTooltip = () => {
    showingPrevTooltip = false;
  };

  let showingNextTooltip = false;
  const showNextTooltip = () => {
    showingNextTooltip = true;
  };
  const hideNextTooltip = () => {
    showingNextTooltip = false;
  };

  export let title = tour.title;
  export let tour;
  export let sceneState;
  export let updateSceneOrder;
  export let scenes = tour.scenes?.filter((scene) => !scene.hidden && !scene.burried) || [];
  export let nextScene = () => {
    tourFunctions.nextSceneWithOrder(sceneState.currentOrder);
    preloader.preloadCurrentScene();
  };
  export let prevScene = () => {
    tourFunctions.prevSceneWithOrder(sceneState.currentOrder, 'prev');
    preloader.preloadCurrentScene();
  };


  let currentSceneName;
  function getCurrentSceneName() {
    currentSceneName = tourFunctions.currentScene.getName();
    return currentSceneName;
  }

  let currentCategory = null;
  let categoryInput;
  export let categories = tour.categories || []; // categories found in scenes
  let visibleScenes = scenes; // scenes for the current category
  export let hasCategories = tour.features?.includes("categories") && categories.length > 0;
  export let categoryStyle = "sidebar"; // select, buttons, sidebar
  let thumbsButton; // ref to thumbs button
  const preloader = new Preloader(tour);
  if (isMobile) categoryStyle = "select";
  
  // Personalization state
  let selectedTags = [];
  let selectedRoute = null;
  let showTags = false;
  let showPersonalizedTour = false;
  let searchScenes = [];
  let isGenerating = false;
  let hasPersonalization = false;
  let searchInput = ''; // Add search input state
  let isPersonalizationActive = false; // Track if personalization view is currently active
  let isPersonalizationOnlyMode = false; // Track if personalization was triggered from external button
  
  // Animated placeholder for interests input
  let currentPlaceholderIndex = 0;
  let placeholderInterval;
  let isInputFocused = false;
  let animationKey = 0; // Force re-render of animations
  
  const collegeExamples = [
    "What do you want to see?",
    "Show me the best study spots on campus",
    "I want to find the computer science department",
    "Show me the dining halls and food options",
    "I'm looking for popular student hangouts",
    "Show me the libraries and study areas",
    "I want to see the sports facilities and gym",
    "Show me available clubs and organizations",
    "I need to find parking on campus",
    "Show me the residence halls and dorms",
    "I want to find places to meet other students"
  ];

  export let showPrevNext = true;
  export let thumbsButtonOpen = $i18n.t('tour.openScenes');
  export let thumbsButtonClose = $i18n.t('tour.closeScenes');
  
  // Reactive statement to determine the correct close button text
  $: thumbsButtonCloseText = isPersonalizationOnlyMode ? $i18n.t('tour.closePersonalization') : thumbsButtonClose;
  
  const tourId = tour.id;
  let thumbsgrid; //bind on .thumbsgrid for scrolling behavior
  var thumbs = []; //bind on .thumbs for scrolling behavior
  let visible = false; // visibility of thumbs grid

  // Flag to track if user has explicitly selected "All" category
  let hasExplicitlySelectedAll = false;

  // Check if tour has personalization features
  $: hasPersonalization = Boolean(tour.features?.includes('personalizationButton') || (tour.features?.includes('routes') && tour.routes && tour.routes.length > 0));
  $: hasCategorySidebar = (hasCategories || hasPersonalization) && categoryStyle === "sidebar" && !isPersonalizationOnlyMode;

  // Filter out "Must See" tag from selectable tags
  $: selectableTags = tour.tags?.filter(tag => tag.name !== 'Must See') || [];

  // Get "Must See" tag ID for automatic inclusion
  $: mustSeeTagId = tour.tags?.find(tag => tag.name === 'Must See')?.id;

  // Check if generate button should be enabled
  $: canGenerateTour = searchInput.trim().length > 0;
  
  // Start animated placeholder when personalization is active and input is not focused
  $: if (isPersonalizationActive && !isInputFocused && !searchInput.trim()) {
    startPlaceholderAnimation();
  } else {
    stopPlaceholderAnimation();
  }
  
  // Stop animation when user starts typing
  $: if (searchInput.trim()) {
    stopPlaceholderAnimation();
  }
  
  // Functions for animated placeholder
  function startPlaceholderAnimation() {
    if (placeholderInterval) return; // Already running
    
    // Start with a delay to make it feel more natural
    setTimeout(() => {
      if (!isInputFocused && !searchInput.trim()) {
        placeholderInterval = setInterval(() => {
          currentPlaceholderIndex = (currentPlaceholderIndex + 1) % collegeExamples.length;
          // Force animation restart on each text change
          animationKey++;
        }, 3000); // Change every 3 seconds
      }
    }, 1000); // 1 second delay before starting
  }
  
  function stopPlaceholderAnimation() {
    if (placeholderInterval) {
      clearInterval(placeholderInterval);
      placeholderInterval = null;
    }
  }
  
  function handleInputFocus() {
    isInputFocused = true;
    stopPlaceholderAnimation();
  }
  
  function handleInputBlur() {
    isInputFocused = false;
    // Restart animation if personalization is still active and input is empty
    if (isPersonalizationActive && !searchInput.trim()) {
      startPlaceholderAnimation();
    }
  }
  
  // Force animation restart when animationKey changes
  $: if (animationKey > 0) {
    // Force animation restart by manipulating the DOM element
    setTimeout(() => {
      const overlay = document.querySelector('.placeholder-overlay');
      if (overlay) {
        // Remove and re-add the slide-in class to force restart
        overlay.classList.remove('slide-in');
        // Force reflow
        overlay.offsetHeight;
        overlay.classList.add('slide-in');
      }
    }, 10);
  }
  
  // Cleanup on component destroy
  onDestroy(() => {
    stopPlaceholderAnimation();
  });

  // Create separate lists for personalized and remaining scenes
  $: personalizedScenes = (() => {
    if (!showPersonalizedTour) return [];
    
    const personalized = [];
    
    // 1. Add search results ordered by similarityScore
    personalized.push(...searchScenes);
    
    // 2. Add must-see scenes
    if (mustSeeTagId) {
      scenes.forEach(scene => {
        if (scene.tagIds?.includes(mustSeeTagId) && 
            !searchScenes.some(sScene => sScene.id === scene.id) &&
            !personalized.some(pScene => pScene.id === scene.id)) {
          personalized.push(scene);
        }
      });
    }
    
    return personalized;
  })();

  $: remainingScenes = (() => {
    if (!showPersonalizedTour) return [];
    
    // For featured routes, get scenes not in the selected route
    if (selectedRoute) {
      return scenes.filter(scene => 
        !selectedRoute.sceneIds.includes(scene.id)
      );
    }
    
    // For personalized tours, get scenes not in the personalized list
    return scenes.filter(scene => 
      !personalizedScenes.some(pScene => pScene.id === scene.id)
    );
  })();

  // Update sceneState when personalized tour changes (called manually, not reactively)
  function updateSceneStateFromPersonalization() {
    setTimeout(() => {
      if (showPersonalizedTour) {
        if (selectedRoute) {
          // For featured routes, create navigation order: route scenes first, then remaining scenes
          const routeScenes = selectedRoute.sceneIds
            .map(sceneId => scenes.find(scene => scene.id === sceneId))
            .filter(Boolean);
          const remainingScenes = scenes.filter(scene => 
            !selectedRoute.sceneIds.includes(scene.id)
          );
          const navigationOrder = [...routeScenes, ...remainingScenes];
          updateSceneOrder(navigationOrder, true);
        } else if (personalizedScenes.length > 0) {
          // For personalized tours, create navigation order: personalized scenes first, then remaining scenes
          const navigationOrder = [...personalizedScenes, ...remainingScenes];
          updateSceneOrder(navigationOrder, true);
        }
      } else if (!showPersonalizedTour && sceneState.isPersonalizedTour) {
        // Only reset if we were previously in personalized mode
        updateSceneOrder(scenes, false);
      }
    }, 0);
  }

  // Fire event every time thumb grid visibility changes
  $: if (visible || visible == false) {
    dispatchEvent(
      new CustomEvent("krpano:gridtoggle", {
        detail: { grid: visible },
      })
    );
  }

  function changeCategory(category) {
    currentCategory = category;

    if (category === 'personalize') {
      isPersonalizationActive = true;
      showTags = true;
      hasExplicitlySelectedAll = false; // Reset flag when switching to personalization
      
      // If we have a personalized tour or featured route active, restore the scene order
      if (showPersonalizedTour) {
        if (selectedRoute) {
          // For featured routes, restore route scenes + remaining scenes
          const routeScenes = selectedRoute.sceneIds
            .map(sceneId => scenes.find(scene => scene.id === sceneId))
            .filter(Boolean);
          const remainingScenes = scenes.filter(scene => 
            !selectedRoute.sceneIds.includes(scene.id)
          );
          const navigationOrder = [...routeScenes, ...remainingScenes];
          updateSceneOrder(navigationOrder, true);
        } else if (personalizedScenes.length > 0) {
          // For personalized tours, restore personalized scenes + remaining scenes
          const navigationOrder = [...personalizedScenes, ...remainingScenes];
          updateSceneOrder(navigationOrder, true);
        }
      }
      
      return;
    }

    if (category) {
      const categoryObj = categories.find((c) => c.id === category.id);
      if (categoryObj?.sceneIds?.length > 0) {
        // New approach using sceneIds
        visibleScenes = categoryObj.sceneIds
          .map((sceneId) => scenes.find((scene) => scene.id === sceneId))
          .filter(Boolean);
      } else {
        // Old approach using category name
        visibleScenes = scenes.filter((scene) => scene.category === category.name);
      }
      hasExplicitlySelectedAll = false;
    } else {
      visibleScenes = Object.keys(scenes).length > 0 ? Object.values(scenes) : scenes; // Show all scenes
      hasExplicitlySelectedAll = true;
    }

    // Hide personalization view when switching to regular categories
    isPersonalizationActive = false;
    isPersonalizationOnlyMode = false; // Reset personalization-only mode when switching categories
    
    // If we had a personalized tour active, switch back to default scene order
    if (sceneState.isPersonalizedTour) {
      updateSceneOrder(scenes, false);
    }
    
    return visibleScenes;
  }

  function openThumbs() {
    if (scenes.length === 0) {
      scenes = window.CC.krpano.get("scene").getArray();
      visibleScenes = scenes;
      // announce the opening of the thumbs grid
      document.getElementById("liveFeedback").textContent =
        $i18n.t("tour.openedScenes");
    }

    getCurrentSceneName();
    scrollToActiveThumb();    
    visible = true;
    
    // Reset personalization-only mode when opening normally (not through personalization button)
    if (!isPersonalizationActive) {
      isPersonalizationOnlyMode = false;
    }
  }

  function closeThumbs() {
    visible = false;
    // announce the closing of the thumbs grid
    document.getElementById("liveFeedback").textContent =
      $i18n.t("tour.closedScenes");
    tourFunctions.setStopUrl();
    tourFunctions.focus();
  }

  function toggleThumbs() {
    if (visible) {
      closeThumbs();
    } else {
      // Reset personalization-only mode when manually toggling
      isPersonalizationOnlyMode = false;
      openThumbs();
    }
  }

  function sleep(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  function getActiveSceneIndex() {
    if (showPersonalizedTour && isPersonalizationActive) {
      // In personalized tour mode, check both personalized and remaining scenes
      const personalizedIndex = personalizedScenes.findIndex((scene) => scene.name === currentSceneName);
      if (personalizedIndex !== -1) {
        return personalizedIndex;
      }
      const remainingIndex = remainingScenes.findIndex((scene) => scene.name === currentSceneName);
      if (remainingIndex !== -1) {
        return personalizedScenes.length + remainingIndex;
      }
      return -1;
    } else {
      // In regular mode (categories, all scenes, etc.), use visibleScenes
      return visibleScenes.findIndex((scene) => scene.name === currentSceneName);
    }
  }

  function scrollToActiveThumb() {
    let activeSceneIndex = getActiveSceneIndex();
    if (activeSceneIndex !== -1 && thumbs[activeSceneIndex]) {
      window.scrollthumb = thumbs[activeSceneIndex];
      thumbs[activeSceneIndex].scrollIntoView();
    }
  }

  function loadScene(scene) {
    tourFunctions.loadScene(scene, 'grid');
    // announce the scene change
    document.getElementById("liveFeedback").textContent =
      `${$i18n.t("tour.loadScene")} ${scene.title}`;
    
    // If we're not in personalization mode and we have a personalized tour active,
    // switch back to using the default scene order for navigation
    if (!isPersonalizationActive && sceneState.isPersonalizedTour) {
      updateSceneOrder(scenes, false);
    }
    
    closeThumbs();
    preloader.preloadCurrentScene();
  }

  // Personalization functions
  function handleRouteSelection(route) {
    if (selectedRoute && selectedRoute.id === route.id) {
      // If same route is clicked, deselect it and return to full tour
      selectedRoute = null;
      showPersonalizedTour = false;
      updateSceneOrder(scenes, false);
    } else {
      // Select the new route and immediately set scene state
      selectedRoute = route;
      showPersonalizedTour = true;
      
      // Get scenes for this route
      const routeScenes = route.sceneIds
        .map(sceneId => scenes.find(scene => scene.id === sceneId))
        .filter(Boolean);
      
      // Get remaining scenes (scenes not in this route)
      const remainingScenes = scenes.filter(scene => 
        !route.sceneIds.includes(scene.id)
      );
      
      // Set the scene order to include route scenes first, then remaining scenes
      const navigationOrder = [...routeScenes, ...remainingScenes];
      updateSceneOrder(navigationOrder, true);
    }
  }

  function handleTagSelection(tagId) {
    // If empty string is passed, clear all tags
    if (tagId === '') {
      selectedTags = [];
      showPersonalizedTour = false; // Reset personalized tour when tags are cleared
      return;
    }

    selectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];
    
    // Reset personalized tour when tags change
    showPersonalizedTour = false;
  }

  function generatePersonalizedTour() {
    if (!canGenerateTour) return;

    isGenerating = true;

    // Function to search scenes via API
    const searchScenesViaAPI = async () => {
      if (!searchInput.trim() || !tourId) return [];
      
      try {
        // return test data for development
        if (process.env.NODE_ENV === 'development') {
          const testData = [
            {
              id: '663ff2311ec0e01a61dadf67',
              similarityScore: 0.9,
            },
            {
              id: '663ff2311ec0e01a61dadf59',
              similarityScore: 0.8,
            },
            {
              id: '663ff2311ec0e01a61dadf65',
              similarityScore: 0.7,
            },
          ];
          return testData.map(result => {
            const scene = scenes.find(s => s.id === result.id);
            return scene ? { ...scene, similarityScore: result.similarityScore } : null;
          }).filter(Boolean);
        }
        const response = await fetch(`https://api.vntpoint.com/tours/${tourId}/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: searchInput.trim(),
            limit: 4
          })
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }

        const searchResults = await response.json();
        
        // Map API results to scene objects
        return searchResults.map(result => {
          const scene = scenes.find(s => s.id === result.id);
          return scene ? { ...scene, similarityScore: result.similarityScore } : null;
        }).filter(Boolean);
      } catch (error) {
        console.error('Error searching scenes:', error);
        return [];
      }
    };

    // Main generation logic
    const generateTour = async () => {
      try {
        const response = await searchScenesViaAPI();
        // Store search results with similarity scores for proper sorting
        searchScenes = response
          .filter(scene => scene.similarityScore !== undefined)
          .sort((a, b) => b.similarityScore - a.similarityScore);
        showPersonalizedTour = true;
        // Update scene state after setting showPersonalizedTour
        updateSceneStateFromPersonalization();
      } catch (error) {
        console.error('Error generating personalized tour:', error);
        // Fallback to empty results
        searchScenes = [];
        showPersonalizedTour = true;
        // Update scene state after setting showPersonalizedTour
        updateSceneStateFromPersonalization();
      } finally {
        isGenerating = false;
      }
    };

    // Start generation with a small delay for UX
    setTimeout(generateTour, 500);
  }

  function handleBackToTags() {
    showPersonalizedTour = false;
    selectedRoute = null;
    // Keep showTags true so we stay in personalization mode but show route selection
    // Update scene state after clearing personalized tour
    updateSceneStateFromPersonalization();
  }

  function clearPersonalizationState() {
    showTags = false;
    showPersonalizedTour = false;
    isPersonalizationActive = false;
    selectedTags = [];
    selectedRoute = null;
    searchInput = '';
    searchScenes = [];
    hasExplicitlySelectedAll = false;
    // Update scene state after clearing personalized tour
    updateSceneStateFromPersonalization();
  }

  function toggleTags() {
    if (showPersonalizedTour) {
      // If we have personalized results, show them directly
      isPersonalizationActive = true;
      showTags = true;
      currentCategory = 'personalize';
      
      // Restore the personalized scene order
      if (personalizedScenes.length > 0) {
        const navigationOrder = [...personalizedScenes, ...remainingScenes];
        updateSceneOrder(navigationOrder, true);
      }
    } else {
      // Otherwise, toggle between tag selection and regular view
      isPersonalizationActive = !isPersonalizationActive;
      showTags = isPersonalizationActive;
      if (isPersonalizationActive) {
        currentCategory = 'personalize';
      } else {
        currentCategory = null;
      }
    }
  }

  function handleCategoryChange(category) {
    if (category === 'personalize') {
      toggleTags();
    } else if (category === undefined || category === null) {
      // "All" category case
      changeCategory(null);
    } else {
      changeCategory(category);
    }
  }

  // Expose methods for external use
  export { openThumbs, activatePersonalization, activateCategory, activateCategoryAll };

  // Method to activate personalization from external call (e.g., hash)
  function activatePersonalization() {
    try {
      isPersonalizationActive = true;
      showTags = true;
      currentCategory = 'personalize';
      hasExplicitlySelectedAll = false; // Reset flag when switching to personalization
      isPersonalizationOnlyMode = true; // Set flag to hide categories
      
      // If we have a personalized tour generated, restore the personalized scene order
      if (showPersonalizedTour && personalizedScenes.length > 0) {
        const navigationOrder = [...personalizedScenes, ...remainingScenes];
        updateSceneOrder(navigationOrder, true);
      }
      
      // Log for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('Personalization activated via external button');
      }
    } catch (error) {
      console.error('Error activating personalization:', error);
    }
  }

  // Helper function to get category information for debugging
  function getCategoryInfo() {
    const totalCategories = categories?.length || 0;
    const categoryNames = categories?.map((cat, index) => `${index + 1}: ${cat.name}`).join(', ') || 'none';
    return { totalCategories, categoryNames };
  }

  // Method to activate category by index from external call (e.g., hash)
  function activateCategory(categoryIndex) {
    try {
      // Validate category index
      if (categoryIndex < 0) {
        console.warn(`Invalid category index: ${categoryIndex}. Must be >= 0`);
        return;
      }
      
      // Reset personalization state
      isPersonalizationActive = false;
      showTags = false;
      isPersonalizationOnlyMode = false; // Reset flag
      
      // Handle "All" category (index 0)
      if (categoryIndex === 0) {
        currentCategory = null;
        visibleScenes = scenes;
        hasExplicitlySelectedAll = true; // Mark that "All" was explicitly selected
        if (process.env.NODE_ENV === 'development') {
          console.log('All category activated via hash');
        }
        return;
      }
      
      // Handle regular categories (index 1+)
      const actualIndex = categoryIndex - 1; // Convert to 0-based index
      if (categories && categories.length > 0 && categories[actualIndex]) {
        currentCategory = categories[actualIndex];
        hasExplicitlySelectedAll = false; // Reset flag when selecting specific category
        changeCategory(currentCategory);
        if (process.env.NODE_ENV === 'development') {
          console.log(`Category ${categoryIndex} (${currentCategory.name}) activated via hash`);
        }
      } else {
        const { totalCategories, categoryNames } = getCategoryInfo();
        console.warn(`Category index ${categoryIndex} not found. Available categories: ${totalCategories} (${categoryNames})`);
        // Fallback to "All" category
        currentCategory = null;
        visibleScenes = scenes;
        hasExplicitlySelectedAll = true; // Mark that "All" was explicitly selected
      }
    } catch (error) {
      console.error('Error activating category:', error);
      // Fallback to "All" category on error
      currentCategory = null;
      visibleScenes = scenes;
      hasExplicitlySelectedAll = true; // Mark that "All" was explicitly selected
    }
  }

  // Method to activate "All" category from external call (e.g., hash)
  function activateCategoryAll() {
    try {
      // Reset personalization state
      isPersonalizationActive = false;
      showTags = false;
      isPersonalizationOnlyMode = false; // Reset flag
      
      // Set to "All" category
      currentCategory = null;
      visibleScenes = scenes;
      hasExplicitlySelectedAll = true; // Mark that "All" was explicitly selected
      
      if (process.env.NODE_ENV === 'development') {
        console.log('All category activated via hash (#/category/all)');
      }
    } catch (error) {
      console.error('Error activating All category:', error);
    }
  }

  function updateHash() {
    if (!visible) return;
    
    if (isPersonalizationActive) {
      window.location.hash = "/personalization";
    } else if (currentCategory === null && hasExplicitlySelectedAll) {
      window.location.hash = "/category/all";
    } else if (currentCategory !== null) {
      const categoryIndex = categories.findIndex(cat => cat.id === currentCategory.id);
      if (categoryIndex !== -1) {
        window.location.hash = `/category/${categoryIndex + 1}`;
      }
    }
  }

  // Watch for state changes and update hash
  $: if (visible && (isPersonalizationActive !== undefined || currentCategory !== undefined)) {
    updateHash();
  }

  // keyboard shortcuts space opens/closes thumbs
  document.addEventListener("keydown", function (event) {
    // ? / key opens/closes thumbs
    if (event.keyCode === 191) {
      toggleThumbs();
    }

    // Escape key
    if (event.keyCode === 27) {
      visible = false;
    }
  });

  let thumbsContainer;
  let stillAnimating = false;
  let focusTrapInstance = null; // to hold the focus trap instance

  onMount(() => {
    thumbsContainer.addEventListener("transitionstart", () => {
      stillAnimating = true;
    });
    thumbsContainer.addEventListener("transitionend", () => {
      stillAnimating = false;
    });
  });

  async function handleFocus() {
    await tick();
    const activeLocationThumb = thumbs[getActiveSceneIndex()];
    // only allow one focus trap instance at a time
    if (focusTrapInstance) {
      focusTrapInstance.destroy();
      focusTrapInstance = null;
    }
    focusTrapInstance = focusTrap(thumbsContainer);
    // there are cases where the active thumb is not focusable (e.g. category hides the scene thumb)
    if (activeLocationThumb) {
      activeLocationThumb.querySelector("button").focus();
    }
  }
  $: if (visible && scenes.length && thumbs.length && !stillAnimating) {
    handleFocus();
  }
</script>

<div
  class="thumbs-container"
  class:active={visible}
  class:is-personalization-only={isPersonalizationOnlyMode}
  bind:this={thumbsContainer}
  role="dialog"
  aria-label={$i18n.t("tour.openScenes")}
>
  <header>
    <!-- Title -->
    {#if !isPersonalizationOnlyMode}
      <h1 class="tour-title">{@html title}</h1>
    {/if}
    <!-- Categories -->
    {#if hasCategories && !isPersonalizationOnlyMode}
      <div class="scene-categories">
        <!-- buttons -->
        {#if categoryStyle === "buttons"}
          <h2>{$i18n.t("tour.selectCategory")}:</h2>
          <ol>
            <li class:active={!currentCategory}>
              <button
                class="category"
                on:click={() => changeCategory()}
                tabindex="0"
              >
                {$i18n.t("tour.categoryAll")}
              </button>
            </li>
            {#each categories as c}
              <li class:active={currentCategory.name === c.name}>
                <button
                  class="category"
                  on:click={() => changeCategory(c)}
                  tabindex="0"
                >
                  {c.name}
                </button>
              </li>
            {/each}
          </ol>
          <!-- select -->
        {:else if categoryStyle === "select"}
          <div class="select-container">
            <select
              id="categorySelect"
              bind:value={currentCategory}
              on:change={() => {
                if (currentCategory === 'personalize') {
                  toggleTags();
                } else {
                  changeCategory(currentCategory);
                }
              }}
              bind:this={categoryInput}
            >
              <option value={null}>{$i18n.t("tour.categoryAll")}</option>
              {#if hasPersonalization}
                <option value="personalize">{$i18n.t("tour.personalize")}</option>
              {/if}
              {#each categories as c}
                <option value={c}>{c.name}</option>
              {/each}
            </select>
            {#if hasPersonalization && isPersonalizationActive && categoryStyle === "select"}
              <button
                class="redo-button"
                on:click={handleBackToTags}
                aria-label={$i18n.t('tour.startOver')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                  <path d="M21 3v5h-5"/>
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                  <path d="M3 21v-5h5"/>
                </svg>
              </button>
            {/if}
          </div>
        {/if}
      </div>
    {/if}
  </header>

  <main
    class:has-category-sidebar={hasCategorySidebar}
  >
    {#if hasCategorySidebar}
      <nav class="category-sidebar">
        <h2>{$i18n.t('tour.selectCategory')}:</h2>
        <ol>
          <li class:active={!currentCategory && !isPersonalizationActive}>
            <button
              tabindex="0"
              on:click={() => handleCategoryChange()}
              class="category"
            >
              {$i18n.t("tour.categoryAll")}
            </button>
          </li>
          {#each categories as c}
            <li class:active={currentCategory?.name === c?.name && !isPersonalizationActive}>
              <button
                tabindex="0"
                on:click={() => handleCategoryChange(c)}
                class="category"
              >
                {c.name}
              </button>
            </li>
          {/each}
          {#if hasPersonalization}
            <li class:active={isPersonalizationActive}>
              <button
                tabindex="0"
                on:click={toggleTags}
                class="category category-personalize"
              >
                <div class="icon w-6 h-6 mr-2 inline-block">
                  <AiIcon color="currentColor" />
                </div>
                {$i18n.t("tour.personalize")}
              </button>
            </li>
          {/if}
        </ol>
      </nav>
    {/if}

    <!-- Content Container -->
    <div class="content-container">
      <!-- Personalization Tags or Personalized Tour View -->
      {#if hasPersonalization && isPersonalizationActive}
        {#if isGenerating}
          <!-- Generating Tour View -->
          <div class="generating-tour">
            <div class="generating-content">
              <div class="generating-card">
                <div class="spinner"></div>
                <h2>{$i18n.t('tour.generatingTour')}</h2>
                <p>{$i18n.t('tour.generatingTourDescription')}</p>
              </div>
            </div>
          </div>
        {:else if showPersonalizedTour}
          <!-- Personalized Tour View -->
          <div class="personalized-tour-view">
            <div class="tour-sections">
              <div class="tour-header">
                <div class="header-content">
                  {#if selectedRoute}
                    <h2>{selectedRoute.title} Route</h2>
                    <p class="tour-description">
                      Navigate through {selectedRoute.sceneIds.length} scenes in this featured route
                    </p>
                  {:else}
                    <h2>{$i18n.t('tour.yourPersonalizedTour')}</h2>
                    <p class="tour-description">
                      {$i18n.t('tour.personalizedTourDescription')}
                    </p>
                  {/if}
                </div>
                <div class="back-button-container">
                  <button 
                    class="back-button"
                    on:click={handleBackToTags}
                  >
                    <div class="icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                        <path d="M21 3v5h-5"/>
                        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                        <path d="M3 21v-5h5"/>
                      </svg>
                    </div>
                    {$i18n.t('tour.startOver')}
                  </button>
                </div>
              </div>
              <!-- Personalized Tour Section -->
              {#if (selectedRoute && selectedRoute.sceneIds) || personalizedScenes.length > 0}
                <section class="personalized-section">
                  <nav class="thumbsgrid">
                    <ol class="thumbsgrid-view">
                      {#if selectedRoute}
                        <!-- Show route scenes -->
                        {#each selectedRoute.sceneIds as sceneId, i}
                          {@const s = scenes.find(scene => scene.id === sceneId)}
                          {#if s}
                            <li class:active={currentSceneName === s.name} bind:this={thumbs[i]}>
                              <button
                                on:click={loadScene(s)}
                                tabindex="0"
                                class="thumb"
                                aria-label={`${$i18n.t('tour.openScene')} ${s.title}`}
                              >
                                <figure>
                                  <img
                                    src={s.thumbUrl || s.thumburl}
                                    width="150"
                                    height="150"
                                    alt={s.title}
                                  />
                                  <figcaption>
                                    <p>{@html tourFunctions.parseContent(s.title)}</p>
                                  </figcaption>
                                </figure>
                              </button>
                            </li>
                          {/if}
                        {/each}
                      {:else}
                        <!-- Show search-based personalized scenes -->
                        {#each personalizedScenes as s, i}
                          {@const isSearchResult = s.similarityScore !== undefined}
                          {@const isMustSee = mustSeeTagId && s.tagIds?.includes(mustSeeTagId)}
                          {@const shouldShowPersonalizedBadge = isSearchResult || isMustSee}
                          {@const badgeText = isMustSee ? 'Must See' : $i18n.t('tour.personalized')}
                          <li class:active={currentSceneName === s.name} class:personalized={shouldShowPersonalizedBadge} bind:this={thumbs[i]}>
                            <button
                              on:click={loadScene(s)}
                              tabindex="0"
                              class="thumb"
                              aria-label={`${$i18n.t('tour.openScene')} ${s.title}`}
                            >
                              <figure>
                                <img
                                  src={s.thumbUrl || s.thumburl}
                                  width="150"
                                  height="150"
                                  alt={s.title}
                                />
                                {#if shouldShowPersonalizedBadge}
                                  <div class="personalized-badge">
                                    <div class="badge-icon">
                                      <AiIcon color="#FFFFFF" />
                                    </div>
                                    <span>{badgeText}</span>
                                  </div>
                                {/if}
                                <figcaption>
                                  <p>{@html tourFunctions.parseContent(s.title)}</p>
                                </figcaption>
                              </figure>
                            </button>
                          </li>
                        {/each}
                      {/if}
                    </ol>
                  </nav>
                </section>
              {/if}

              <!-- Remaining Locations Section -->
              {#if remainingScenes.length > 0}
                <section class="remaining-section">
                  <div class="remaining-header">
                    <div class="header-content">
                      <h2>{$i18n.t('tour.remainingLocations')}</h2>
                    </div>
                  </div>
                  <nav class="thumbsgrid">
                    <ol class="thumbsgrid-view">
                      {#each remainingScenes as s, i}
                        <li class:active={currentSceneName === s.name} bind:this={thumbs[personalizedScenes.length + i]}>
                          <button
                            on:click={loadScene(s)}
                            tabindex="0"
                            class="thumb"
                            aria-label={`${$i18n.t('tour.openScene')} ${s.title}`}
                          >
                            <figure>
                              <img
                                src={s.thumbUrl || s.thumburl}
                                width="150"
                                height="150"
                                alt={s.title}
                              />
                              <figcaption>
                                <p>{@html tourFunctions.parseContent(s.title)}</p>
                              </figcaption>
                            </figure>
                          </button>
                        </li>
                      {/each}
                    </ol>
                  </nav>
                </section>
              {/if}
            </div>
          </div>
        {:else}
          <!-- Personalization Tags View -->
          <div class="personalization-tags">
            <div class="tags-content">
              <div class="tags-card">
                <h3>
                  {$i18n.t('tour.personalizedTour')}
                </h3>
                <div class="openai-icon">
                  <OpenAIIcon color="#ffffff" />
                </div>
                <div class="search-container">
                  <div class="input-wrapper">
                    <input
                      type="text"
                      class="interests-input"
                      placeholder={isPersonalizationActive && !isInputFocused && !searchInput.trim() 
                        ? '' 
                        : $i18n.t('tour.whatInterestsYou')}
                      bind:value={searchInput}
                      on:focus={handleInputFocus}
                      on:blur={handleInputBlur}
                      on:keydown={(e) => {
                        if (e.key === 'Enter' && canGenerateTour) {
                          generatePersonalizedTour();
                        }
                      }}
                    />
                    
                    <!-- Animated placeholder overlay -->
                    {#if isPersonalizationActive && !isInputFocused && !searchInput.trim()}
                      <div 
                        class="placeholder-overlay slide-in"
                        key={`${currentPlaceholderIndex}-${animationKey}`}
                      >
                        {collegeExamples[currentPlaceholderIndex]}
                      </div>
                    {/if}
                  </div>
                </div>
                <div class="generate-button-container">
                  <button 
                    class="generate-button"
                    class:active={canGenerateTour}
                    class:disabled={!canGenerateTour}
                    on:click={generatePersonalizedTour}
                    disabled={!canGenerateTour}
                  >
                    {$i18n.t('tour.generatePersonalizedTour')}
                  </button>
                </div>

                {#if tour.routes && tour.routes.filter(route => route.featured === true).length > 0}
                  <h4>Featured Tour Routes</h4>
                  <p class="routes-description">Select a route to navigate through specific scenes in order</p>
                  <div class="routes-container">
                    {#each tour.routes.filter(route => route.featured === true) as route}
                      <button
                        class="route-button"
                        class:selected={selectedRoute && selectedRoute.id === route.id}
                        on:click={() => handleRouteSelection(route)}
                      >
                        {route.title}
                      </button>
                    {/each}
                  </div>
                {/if}
              </div>
            </div>
          </div>
        {/if}
      {:else}
        <!-- Regular Thumbs Grid View -->
        <nav class="thumbsgrid" bind:this={thumbsgrid}>
          <ol class="thumbsgrid-view">
            <!-- Scenes -->
            {#each visibleScenes as s, i}
              <li class:active={currentSceneName === s.name} bind:this={thumbs[i]}>
                <button
                  on:click={loadScene(s)}
                  tabindex="0"
                  class="thumb"
                  aria-label={`${$i18n.t('tour.openScene')} ${s.title}`}
                >
                  <figure>
                    <img
                      src={s.thumbUrl || s.thumburl}
                      width="150"
                      height="150"
                      alt={s.title}
                    />
                    <figcaption>
                      <p>{@html tourFunctions.parseContent(s.title)}</p>
                    </figcaption>
                  </figure>
                </button>
              </li>
            {/each}
          </ol>
        </nav>
      {/if}
    </div>
  </main>
</div>
<!-- /  .thumbs-container -->
<!-- Button -->
<div class="bottom-menu" transition:fly={{ y: 300, duration: 500 }}>
  {#if isMobile || showPrevNext}
    <button
      class="prev-button"
      on:click={() => {
        setTrackSceneSource('previous');
        prevScene();
        visible = false;
      }}
      on:mouseenter={showPrevTooltip}
      on:mouseleave={hidePrevTooltip}
      aria-label={$i18n.t('tour.prevScene')}
    >
      <Tooltip
        text={$i18n.t('tour.prevScene')}
        showingTooltip={showingPrevTooltip}
        position={"top"}
      />
      <!-- Previous Arrow -->
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.71 32.59"
          ><polyline
            points="18.68 1.11 2.21 16.18 18.68 31.49"
            style="fill:none;stroke:var(--thumbs-grid-bottom-menu-text-color);stroke-miterlimit:10;stroke-width:2px"
          /></svg
        >
      </div>
    </button>
  {/if}
  <button
    class="thumbs-button"
    class:active={visible}
    on:click={toggleThumbs}
    on:mouseenter={showThumbsTooltip}
    on:mouseleave={hideThumbsTooltip}
    bind:this={thumbsButton}
    tabindex="0"
  >
    <!-- <Tooltip
      text={visible ? "Close Locations" : "Open Locations"}
      showingTooltip={showingThumbsTooltip}
      position={"top"}
    /> -->
    {#if visible}
      <!-- Close Locations -->
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44.19 44.19"
          ><circle
            cx="22.1"
            cy="22.1"
            r="21.1"
            style="fill:none;stroke:var(--thumbs-grid-bottom-menu-text-color);;stroke-miterlimit:10;stroke-width:2px"
          /><path
            d="M20.63,11.24h3V26.42l5.81-6.1V25.8L22.1,33"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><path
            d="M23.57,11.24h-3V26.42l-5.81-6.1V25.8L22.09,33"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /></svg
        >
      </div>
      {#if !isMobile}<div>{thumbsButtonCloseText}</div>{/if}
    {:else}
      <!-- Open Locations -->
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44.19 44.19"
          ><rect
            x="11.63"
            y="11.58"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="19.64"
            y="11.58"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="27.73"
            y="11.58"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="11.59"
            y="19.59"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="19.59"
            y="19.59"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="27.69"
            y="19.59"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="11.7"
            y="27.61"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="19.7"
            y="27.61"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><rect
            x="27.8"
            y="27.61"
            width="5"
            height="5"
            style="fill:var(--thumbs-grid-bottom-menu-text-color);"
          /><circle
            cx="22.1"
            cy="22.1"
            r="21.1"
            style="fill:none;stroke:var(--thumbs-grid-bottom-menu-text-color);;stroke-miterlimit:10;stroke-width:2px"
          /></svg
        >
      </div>
      {#if !isMobile}<div>{thumbsButtonOpen}</div>{/if}
    {/if}
  </button>
  {#if isMobile || showPrevNext}
    <button
      class="next-button"
      on:click={() => {
        setTrackSceneSource('next');
        nextScene();
        visible = false;
      }}
      on:mouseenter={showNextTooltip}
      on:mouseleave={hideNextTooltip}
      aria-label={$i18n.t('tour.nextScene')}
    >
      <Tooltip
        text={$i18n.t('tour.nextScene')}
        showingTooltip={showingNextTooltip}
        position={"top"}
      />
      <!-- Next Arrow -->
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.71 32.59"
          ><polyline
            points="18.68 1.11 2.21 16.18 18.68 31.49"
            style="fill:none;stroke:var(--thumbs-grid-bottom-menu-text-color);stroke-miterlimit:10;stroke-width:2px"
          /></svg
        >
      </div>
    </button>
  {/if}
</div>

<style>
  /* Component Colors */
  :root {
    --thumbs-grid-text-color: #fff;
    --thumbs-grid-text-bg-color-rgb: 0, 0, 0;
    --thumbs-grid-container-bg-color-rgb: 0, 0, 0;
    --thumbs-grid-bottom-menu-bg-color-rgb: var(--theme-color-rgb);
    --thumbs-grid-bottom-menu-text-color: var(--controls-text-color);
  }

  h1.tour-title {
    text-align: center;
    font-family: var(--font-heading), sans-serif;
    font-size: 2rem;
  }

  div.thumbs-container {
    background: rgba(var(--thumbs-grid-container-bg-color-rgb), 0.8);
    color: white;
    position: fixed;
    width: 100%;
    max-width: 100%;
    height: 100%;
    z-index: 1000;
    left: 0;
    bottom: -101%;
    display: block;
    transition: all 1000ms;
    visibility: hidden;
  }

  .thumbs-container.active {
    bottom: 0;
    visibility: visible;
  }

  .thumbs-container main {
    width: 100%;
    height: 100%;
    height: calc(100% - 165px);
    position: relative;
  }

  .thumbs-container.is-personalization-only main {
    height: 100%;
  }

  .thumbsgrid {
    height: 100%;
    overflow-y: scroll;
    overflow-x: hidden;
  }

  ::-webkit-scrollbar {
    width: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background: #fff;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #ddd;
  }

  .bottom-menu {
    background: rgba(var(--thumbs-grid-bottom-menu-bg-color-rgb), 0.8);
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    z-index: 1001;
    padding: 0.5rem;
    border-radius: 10px 10px 0 0;
    min-width: 185px;
  }

  .bottom-menu:hover {
    background: rgba(var(--thumbs-grid-bottom-menu-bg-color-rgb), 1);
    border: 3px solid var(--thumbs-grid-bottom-menu-text-color);
    border-width: 3px 3px 0 3px;
  }

  button {
    appearance: none;
    margin: 0;
    padding: 0;
    background: none;
    color: inherit;
    font: inherit;
    border: none;
    cursor: pointer;
    display: block;
    text-align: left;
    width: 100%;
    height: auto;
  }

  .thumbs-button {
    /* reset */
    appearance: none;
    margin: 0;
    padding: 0;
    background: none;
    color: inherit;
    font: inherit;
    border: none;
    cursor: pointer;
    display: block;
    text-align: left;
    width: 100%;
    height: auto;
    /* css */
    color: var(--thumbs-grid-bottom-menu-text-color);
    font-family: var(--font-heading), sans-serif;
    font-weight: 300;
    font-size: 1.5rem;
    white-space: nowrap;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 45px;
    min-height: 45px;
    margin: 0 8px;
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 1em;
    height: 1em;
  }

  .icon svg {
    width: 100%;
    height: 100%;
  }


  .thumbs-button .icon {
    width: 35px;
    height: 35px;
    margin-right: 8px;
  }

  .category-personalize .icon {
    margin-right: 8px;
  }

  .next-button .icon svg {
    transform: scaleX(-1);
  }

  .next-button .icon,
  .prev-button .icon {
    width: 35px;
    height: 35px;
  }

  .personalization-tags h3 {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .personalization-tags h3 .icon {
    display: inline-flex;
  }

  .openai-icon {
    width: 70px;
    margin-inline: auto;
  }

  @media (max-width: 600px) {
    .thumbs-button .icon {
      margin-right: 0;
    }
  }

  .thumb {
    /* reset */
    appearance: none;
    margin: 0;
    padding: 0;
    background: none;
    color: inherit;
    font: inherit;
    border: none;
    cursor: pointer;
    display: block;
    text-align: left;
    width: 100%;
    height: 100%;
    /* css */
    border: 3px solid transparent;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    color: var(--thumbs-grid-text-color);
  }
  .personalized .thumb {
    border: 3px solid rgba(var(--theme-color-rgb), 1);
  }

  .thumb:hover,
  .thumb:focus {
    border: 3px solid #fff;
    outline: none;
  }

  .thumb:focus {
    border: 3px solid white !important;
  }

  .thumb:focus-visible {
    outline: 4px solid var(--focus-color);
    outline-offset: 0;
    border-radius: 10px !important;
  }

  /* Hide focus-visible on mobile devices */
  @media (max-width: 600px) {
    .thumb:focus-visible {
      outline: none;
    }
    
    .category:focus-visible {
      outline: none;
    }
    
    .thumbs-button:focus-visible {
      outline: none;
    }
    
    .prev-button:focus-visible,
    .next-button:focus-visible {
      outline: none;
    }
    
    .generate-button:focus-visible {
      outline: none;
    }
    
    .back-button:focus-visible {
      outline: none;
    }
    
    .tag-button:focus-visible {
      outline: none;
    }
    
    .clear-button:focus-visible {
      outline: none;
    }
    
    .interests-input:focus-visible {
      outline: none;
    }
    
    .redo-button:focus-visible {
      outline: none;
    }
  }
  /* END focus-visible */

  .thumbsgrid-view li.active .thumb {
    border: 3px solid #fff;
  }

  .thumbsgrid-view {
    margin: 3rem 0;
    list-style: none;
    margin: 0;
    padding: 0;
    border: none;

    display: grid;
    grid-gap: 0.5rem;
    grid-template-columns: repeat(auto-fill, minmax(275px, 1fr));
    margin: auto;
    width: 95%;
    margin-bottom: 100px;
  }

  .thumbsgrid-view figure {
    width: 100%;
    height: 100%;
  }

  .thumbsgrid-view img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .thumbsgrid-view figure {
    position: relative;
    padding: 0;
    margin: 0;
  }

  .thumbsgrid-view figcaption {
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    text-align: center;
  }

  .thumbsgrid-view figcaption p {
    background: rgba(var(--thumbs-grid-text-bg-color-rgb), 0.8);

    padding: 8px;
    margin: 0;
    display: inline-block;
    border-radius: 5px;
    text-align: center;
    font-size: 1em;
    font-weight: 500;
    font-family: var(--font-body), sans-serif;
  }

  .personalized-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: rgba(var(--theme-color-rgb), 1);
    color: #FFFFFF;
    padding: 4px 8px;
    display: inline-flex;
    font-family: var(--font-body), sans-serif;
    font-weight: 600;
    font-size: 0.75rem;
    border-radius: 12px;
    padding: 4px 8px;
  }



  .badge-icon {
    width: 1em;
    height: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .badge-icon svg {
    width: 100%;
    height: 100%;
  }

  .scene-categories {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  .scene-categories h2 {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.5rem;
    margin: 0;
  }

  .scene-categories ol {
    list-style: none;
    margin: 0.25rem 0;
    border: none;
    padding: 0.5em;
    border-radius: 6px;
  }

  .scene-categories li {
    border: 2px solid #fff;
    display: inline-block;
    padding: 0.25rem 1.25rem;
    margin: 0.15rem;
    cursor: pointer;
    border-radius: 6px;
    font-size: 1.3rem;
    font-family: var(--font-heading), sans-serif;
    background: #fff;
    color: #000;
  }

  .scene-categories li:hover {
    background: #fff;
    color: var(--theme-color);
    border-color: var(--theme-color);
  }

  .scene-categories li.active {
    background: #fff;
    color: #000;
    background: rgba(var(--theme-color-rgb), 0.8);
    color: #fff;
    border-color: rgba(var(--theme-color-rgb), 0.8);
  }

  .scene-categories label {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.5rem;
  }

  .scene-categories select {
    padding: 10px;
    margin: 10px 0;
    border-radius: 5px;
    border: 2px solid #ffffff;
    font-size: 22px;
    min-width: 25%;
    max-width: 95%;
    background: rgba(var(--theme-color-rgb), 0.8);
    color: #ffffff;
    font-family: var(--font-heading), sans-serif;
  }

  .scene-categories select option {
    padding: 8px;
    font-family: var(--font-body), sans-serif;
  }

  .scene-categories select option[value="personalize"] {
    font-weight: 600;
    background-color: rgba(var(--theme-color-rgb), 0.9);
  }

  main.has-category-sidebar {
    display: flex;
  }

  main.has-category-sidebar .thumbsgrid {
    flex: 1;
  }

  .category-sidebar {
    min-width: 200px;
    overflow-y: scroll;
    padding: 10px;
    margin: 0 0 0 18px;
    font-family: var(--font-heading), sans-serif;
  }

  .category-sidebar a {
    font-family: var(--font-body), sans-serif;
    display: block;
    color: #fff;
    padding: 10px;
    text-decoration: none;
    font-size: 1.15rem;
  }

  .category-sidebar a:hover {
    background-color: #555;
    text-decoration: underline;
    border-radius: 8px;
  }

  .category-sidebar ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .category-sidebar li {
  }
  .category-sidebar li:hover {
  }
  .category-sidebar li.active {
  }

  .category-sidebar li.active .category {
    background-color: #000;
    text-decoration: underline;
    border-radius: 8px;
    font-weight: 600;
  }

  .category {
    /* reset */
    appearance: none;
    font-size: inherit;
    color: inherit;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    text-align: inherit;
    text-decoration: inherit;
    cursor: pointer;
    /* css */
    margin: 6px 0;
    padding: 10px;
    font-family: var(--font-body), sans-serif;
    cursor: pointer;
    font-size: 1.15rem;
    color: #fff;
    border: 2px solid transparent;
  }

  .category:hover {
    text-decoration: underline;
    border-radius: 8px;
    border: 2px solid #fff;
  }
  
  .category-personalize {
    display: inline-flex;
    align-items: center;
  }

  /* Content Container */
  .content-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;
  }

  /* Personalization Tags */
  .personalization-tags {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    padding: 2rem;
    box-sizing: border-box;
  }

  .tags-content {
    width: 100%;
    text-align: center;
    max-width: 900px;
  }

  .tags-card {
    background: #FFFFFF;
    border-radius: 1rem;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .tags-card h3, .tags-card h4 {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.75rem;
    font-weight: 900;
    margin-bottom: 0.5rem;
    color: #000000;
  }

  .tags-card h4 {
    font-size: 1.25rem;
  }

  .tags-card .description, .routes-description {
    font-family: var(--font-body), sans-serif;
    font-size: 1rem;
    color: #000000;
    opacity: 0.8;
    margin-bottom: 2rem;
  }

  .tags-container, .routes-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
    margin-bottom: 2rem;
  }

  .tag-button, .route-button {
    width: fit-content;
    padding: 0.5rem 1rem;
    border-radius: 3rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: var(--font-body), sans-serif;
    white-space: nowrap;
    background-color: rgba(0, 0, 0, 0.1);
    border: 2px solid transparent;
    color: #000000;
  }

  .tag-button:hover {
    transform: scale(1.05);
    background-color: rgba(0, 0, 0, 0.15);
  }

  .route-button:hover {
    transform: scale(1.05);
    background-color: #000000;
    color: #FFFFFF;
  }

  .tag-button.selected, .route-button.selected {
    background-color: #000000;
    color: #FFFFFF;
    border: 2px solid #000000;
  }

  .clear-button {
    width: fit-content;
    padding: 0.5rem 1rem;
    border-radius: 3rem;
    border: 2px solid #000000;
    background: transparent;
    color: #000000;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: var(--font-body), sans-serif;
    white-space: nowrap;
  }

  .clear-button:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  .search-container {
    width: 100%;
    max-width: 500px;
    margin: 1rem auto 2rem;
  }

  .interests-input {
    width: 100%;
    padding: 0.75rem 1.25rem;
    border-radius: 3rem;
    border: 2px solid rgba(0, 0, 0, 0.3);
    background: transparent;
    font-family: var(--font-body), sans-serif;
    font-size: 1rem;
    outline: none;
    transition: all 0.3s ease;
    color: #000000;
    box-sizing: border-box;
  }

  .interests-input::placeholder {
    color: #000000;
    opacity: 0.5;
    transition: opacity 0.3s ease-in-out;
  }
  
  /* Slide-in animation for placeholder overlay */
  .placeholder-overlay.slide-in {
    animation: slideInFromRight 0.8s ease-out;
  }
  
  @keyframes slideInFromRight {
    0% { 
      opacity: 0;
      transform: translateY(-50%) translateX(30px);
    }
    100% { 
      opacity: 0.7;
      transform: translateY(-50%) translateX(0);
    }
  }

  .interests-input:focus {
    border-color: #000000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
  
  /* Input wrapper for overlay positioning */
  .input-wrapper {
    position: relative;
    width: 100%;
  }
  
  /* Placeholder overlay that can be animated */
  .placeholder-overlay {
    position: absolute;
    top: 50%;
    left: 1.25rem;
    transform: translateY(-50%);
    pointer-events: none;
    color: #000000;
    opacity: 0.5;
    font-family: var(--font-body), sans-serif;
    font-size: 1rem;
    white-space: nowrap;
    overflow: hidden;
    max-width: calc(100% - 2.5rem);
  }
  
  

  .generate-button-container {
    display: flex;
    justify-content: center;
  }

  .generate-button {
    text-align: center;
    padding: 0.75rem 1.5rem;
    border-radius: 3rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: var(--font-heading), sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    min-width: 160px;
    width: fit-content;
    background-color: rgba(var(--theme-color-rgb), 1);
    border: 3px solid transparent;
  }

  .generate-button:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    border: 3px solid #fff;
  }

  .generate-button.disabled {
    background-color: rgba(0, 0, 0, 0.2);
    color: #FFFFFF;
    opacity: 0.7;
    cursor: not-allowed;
  }

  .generate-button.disabled:hover {
    transform: none;
    box-shadow: none;
  }

  /* Generating Tour */
  .generating-tour {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    padding: 2rem;
  }

  .generating-content {
    text-align: center;
    max-width: 600px;
    width: 100%;
  }

  .generating-card {
    background: #FFFFFF;
    border-radius: 1rem;
    padding: 2.5rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-top: 4px solid #000000;
    border-radius: 50%;
    margin: 0 auto 1.5rem;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .generating-card h2 {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #000000;
  }

  .generating-card p {
    font-family: var(--font-body), sans-serif;
    font-size: 1rem;
    color: #000000;
    opacity: 0.8;
  }

  /* Personalized Tour View */
  .personalized-tour-view {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* tour-header-container removed - using direct tour-header now */

  .tour-header {
    width: 95%;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
  }

  .remaining-header {
    width: 95%;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
  }

  .header-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
    text-align: left;
  }

  .remaining-header .header-content {
    padding: 1rem 0;
  }

  .tour-header .header-content {
    padding-bottom: 1rem;
  }

  @media only screen and (max-width: 600px) {
    .tour-header .header-content {
      padding: 0;
    }
  }

  .tour-header h2 {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.75rem;
    font-weight: 900;
    margin: 0;
    color: var(--thumbs-grid-text-color);
  }

  .remaining-header h2 {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.75rem;
    font-weight: 900;
    margin: 0;
    color: var(--thumbs-grid-text-color);
  }

  .tour-description {
    font-family: var(--font-body), sans-serif;
    font-size: 1rem;
    color: var(--thumbs-grid-text-color);
    opacity: 0.8;
    margin: 0;
  }

  .tour-stats {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
  }

  .stat {
    font-family: var(--font-body), sans-serif;
    font-size: 0.9rem;
    color: var(--thumbs-grid-text-color);
    opacity: 0.7;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .back-button-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 0;
  }

  .back-button {
    width: fit-content;
    padding: 0.65rem 1.5rem;
    border-radius: 3rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: var(--font-heading), sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    min-width: 200px;
    background-color: rgba(var(--theme-color-rgb), 1);
    color: var(--thumbs-grid-text-color);
    border: 3px solid transparent;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  .back-button:hover {
    border: 3px solid #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .back-button .icon {
    width: 1em;
    height: 1em;
    margin-right: 0.5em;
  }

  .tour-sections {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  /* mobile */
  @media only screen and (max-width: 600px) {
    .tour-sections {
      padding: 0;
    }
  }

  .tour-sections section {
    margin-bottom: 0;
  }

  .tour-sections section:last-child {
    margin-bottom: 0;
  }

  .tour-sections h3 {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 1rem;
    color: var(--thumbs-grid-text-color);
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .section-header {
    margin-bottom: 2rem;
    text-align: left;
  }

  .section-header h3 {
    font-family: var(--font-heading), sans-serif;
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    color: var(--thumbs-grid-text-color);
    border-bottom: none;
    padding-bottom: 0;
  }

  .section-description {
    font-family: var(--font-body), sans-serif;
    font-size: 1rem;
    color: var(--thumbs-grid-text-color);
    opacity: 0.8;
    margin: 0;
  }

  .personalized-section {
    margin-bottom: 2rem;
  }

  .personalized-section .thumbsgrid-view {
    margin-bottom: 0;
  }

  .remaining-section {
    margin-bottom: 2rem;
  }

  /* Next / Prev */
  .prev-button,
  .next-button,
  .thumbs-button {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .next-button img {
    transform: scaleX(-1);
  }

  .bottom-menu {
    display: flex;
  }

  @media only screen and (max-width: 600px) {
    .thumbsgrid-view {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }

    .thumbs-container main {
      height: calc(100% - 176px);
    }

    .thumbs-container.is-personalization-only main {
      height: 100%;
    }

    .thumbs-container.is-personalization-only .tour-header .header-content {
      padding-bottom: 0;
    }

    h1.tour-title {
      font-size: 2rem;
    }

    .thumbsgrid-view figcaption p {
      font-size: 12px;
    }

    .thumbs-button {
      font-size: 1.25rem;
    }

    .bottom-menu {
      width: auto;
    }

    .bottom-menu:hover {
      border: none;
    }

    /* Personalization mobile styles */
    .personalization-tags {
      padding: 1rem;
    }

    .tags-content {
      max-width: 100%;
    }

    .tags-card {
      padding: 1.5rem;
    }

    .tags-card h3 {
      font-size: 1.5rem;
    }

    .tags-card .description {
      font-size: 0.9rem;
      margin-bottom: 1.5rem;
    }

    .tags-container {
      margin-bottom: 1.5rem;
    }

    .search-container {
      max-width: 100%;
      margin: 1rem 0 1.5rem;
    }

    .interests-input {
      font-size: 0.9rem;
      padding: 0.6rem 1rem;
    }

    .generate-button {
      padding: 0.6rem 1.5rem;
      font-size: 1rem;
      min-width: 180px;
    }

    .generating-tour {
      padding: 1rem;
      box-sizing: border-box;
    }

    .generating-card {
      padding: 2rem;
      box-sizing: border-box;
    }

    .generating-card h2 {
      font-size: 1.25rem;
    }

    .generating-card p {
      font-size: 0.9rem;
    }

    .tour-header {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 1rem;
      width: 100%;
      margin: 0;
      box-sizing: border-box;
    }
    .header-content {
      flex: 1;
      align-items: flex-start;
      text-align: left;
    }
    .back-button-container {
      flex-shrink: 0;
      margin-top: 0;
    }

    /* Make remaining-header match tour-header on mobile */
    .remaining-header {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 1rem;
      width: 100%;
      margin: 0;
      box-sizing: border-box;
    }

    /* Tour header container is now part of scrollable content */

    .tour-header h2 {
      font-size: 1.75rem; /* Match remaining-header h2 */
    }

    .tour-description {
      display: none; /* Hide description on mobile */
    }

    /* Hide button text on mobile, show only icon */
    .back-button {
      padding: 0.5rem;
      min-width: auto;
      font-size: 0; /* Hide text by making font size 0 */
    }

    .back-button .icon {
      margin-right: 0;
      font-size: 1rem; /* Restore icon size */
    }

    /* Mobile select dropdown styles */
    .scene-categories select {
      font-size: 18px;
      padding: 12px;
      min-width: 200px;
      max-width: 90%;
    }

    .scene-categories select option {
      padding: 10px;
      font-size: 16px;
    }

    .redo-button {
      width: 35px;
      height: 35px;
    }

    .redo-button svg {
      width: 16px;
      height: 16px;
    }
  }

  .select-container {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
  }

  .redo-button {
    border: 2px solid #ffffff;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #ffffff;
  }

  .redo-button:hover {
    background: rgba(var(--theme-color-rgb), 1);
    transform: scale(1.1);
  }

  .redo-button svg {
    width: 20px;
    height: 20px;
  }
</style>
